import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Link } from "wouter";
import { FlaskConical, GraduationCap, Briefcase, Award, ChevronRight, Star } from "lucide-react";

const PATHWAYS = [
  {
    href: "/doctor/career/subspecialties",
    icon: FlaskConical,
    title: "Subspecialty Routes",
    desc: "Explore subspecialty interests within rheumatology: vasculitis, CTD, early inflammatory arthritis, paediatric rheumatology, and academic/research pathways.",
    colour: "text-violet-600",
    bg: "bg-violet-50",
    tags: ["Vasculitis", "CTD", "EIA", "Paediatric", "Academic"],
  },
  {
    href: "/doctor/career/academic",
    icon: FlaskConical,
    title: "Academic Training",
    desc: "ACF, CL, and doctoral pathways for aspiring academic rheumatologists. Understanding the Walport integrated training model and balancing research with clinical work.",
    colour: "text-blue-600",
    bg: "bg-blue-50",
    tags: ["ACF", "CL", "PhD/MD", "NIHR", "MRC"],
  },
  {
    href: "/doctor/career/consultant",
    icon: Briefcase,
    title: "Consultant Readiness",
    desc: "National consultant selection process, job planning, PA/SPA negotiation, and interview preparation for the step to the consultant grade.",
    colour: "text-indigo-600",
    bg: "bg-indigo-50",
    tags: ["National Selection", "Job Planning", "Job Plan", "NHS Consultant"],
  },
  {
    href: "/doctor/career/fellowships",
    icon: Award,
    title: "Fellowships & Honours",
    desc: "FRCP criteria and process, BSR fellowship routes, EULAR schools of rheumatology, and international fellowship opportunities for UK trainees.",
    colour: "text-amber-600",
    bg: "bg-amber-50",
    tags: ["FRCP", "BSR Fellowship", "EULAR Schools", "International"],
  },
  {
    href: "/doctor/career/interview-prep",
    icon: Star,
    title: "Consultant Interview Prep",
    desc: "Structured mock Q&A across the 8 NHS consultant selection leadership domains with sample questions and personal notes.",
    colour: "text-emerald-600",
    bg: "bg-emerald-50",
    tags: ["Leadership", "NHS Domains", "Mock Questions", "Interview"],
  },
];

export default function CareerHub() {
  return (
    <DoctorLayout title="Career Pathways">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-indigo-900 to-indigo-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <GraduationCap className="h-8 w-8 text-indigo-300" />
            <div>
              <h2 className="text-xl font-bold">Career Development Hub</h2>
              <p className="text-indigo-300 text-sm">For ST3–ST7 Registrars & Aspiring Consultants</p>
            </div>
          </div>
          <p className="text-indigo-200 text-sm leading-relaxed">
            Rheumatology offers one of the most intellectually rich and diverse career pathways in medicine. Whether you're drawn to the science of autoimmunity, academic research, paediatric rheumatology, or clinical leadership, this hub maps the routes available to UK trainees and the steps to get there.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PATHWAYS.map((pathway) => (
            <Link key={pathway.href} href={pathway.href}>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group h-full flex flex-col">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`${pathway.bg} ${pathway.colour} h-10 w-10 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                    <pathway.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">{pathway.title}</h3>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 shrink-0 mt-0.5 transition-colors" />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{pathway.desc}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {pathway.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-2">Useful External Resources</h3>
          <ul className="space-y-2 text-sm">
            <li><span className="font-medium text-slate-700">JRCPTB:</span> <a href="https://www.jrcptb.org.uk/specialties/rheumatology" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">jrcptb.org.uk/specialties/rheumatology</a> — official curriculum and ePortfolio</li>
            <li><span className="font-medium text-slate-700">BSR:</span> <a href="https://www.rheumatology.org.uk" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">rheumatology.org.uk</a> — trainee resources, BSR fellowship, annual meeting</li>
            <li><span className="font-medium text-slate-700">EULAR:</span> <a href="https://www.eular.org" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">eular.org</a> — schools of rheumatology, EULAR congress, research grants</li>
            <li><span className="font-medium text-slate-700">NIHR:</span> <a href="https://www.nihr.ac.uk/explore-nihr/support/clinical-research-network.htm" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">nihr.ac.uk</a> — academic clinical fellowships and research funding</li>
          </ul>
        </div>

      </div>
    </DoctorLayout>
  );
}
